'use client';

import { useState, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CredentialsForm } from './CredentialsForm';
import { ProviderLogo } from './ProviderLogo';
import * as api from '@/lib/integrations/api';
import { isOAuthMode } from '@/lib/integrations/types';
import type { ProviderResponse, UserConnection, AuthModeDetails } from '@/lib/integrations/types';
import { getAuthModeLabel } from '@/lib/integrations/utils';
import { Loader2, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';

interface AuthFormProps {
  provider: ProviderResponse;
  onSuccess: (connection: UserConnection) => void;
  onCancel: () => void;
  onError?: (error: string) => void;
}

export function AuthForm({ provider, onSuccess, onCancel, onError }: AuthFormProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionConfig, setConnectionConfig] = useState<Record<string, string>>({});
  const [displayName, setDisplayName] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [baseUrlOverride, setBaseUrlOverride] = useState('');
  const [selectedAuthModeIndex, setSelectedAuthModeIndex] = useState(0);

  // Get available auth modes (from auth_modes array or fallback to single auth_mode)
  const authModes: AuthModeDetails[] = useMemo(() => {
    if (provider.auth_modes && provider.auth_modes.length > 0) {
      return provider.auth_modes;
    }
    // Fallback to single auth mode from provider
    return [{
      mode: provider.auth_mode as AuthModeDetails['mode'],
      label: getAuthModeLabel(provider.auth_mode),
      credentials: provider.credentials,
      connectionConfig: provider.connection_config,
      defaultScopes: provider.default_scopes,
    }];
  }, [provider]);

  const hasMultipleAuthModes = authModes.length > 1;
  const selectedAuthMode = authModes[selectedAuthModeIndex] || authModes[0];

  // Determine current auth mode properties
  const currentAuthMode = selectedAuthMode?.mode || provider.auth_mode;
  const isOAuth = isOAuthMode(currentAuthMode);
  const currentCredentials = selectedAuthMode?.credentials || provider.credentials;
  const currentConnectionConfig = selectedAuthMode?.connectionConfig || provider.connection_config;
  const currentDefaultScopes = selectedAuthMode?.defaultScopes || provider.default_scopes;
  const hasConnectionConfig = currentConnectionConfig && currentConnectionConfig.length > 0;

  // Check if all required connection_config fields are filled
  const isConnectionConfigValid = useCallback(() => {
    if (!hasConnectionConfig || !currentConnectionConfig) return true;
    return currentConnectionConfig.every(
      (field) => !field.required || connectionConfig[field.name]?.trim()
    );
  }, [hasConnectionConfig, currentConnectionConfig, connectionConfig]);

  // Reset connection config when auth mode changes
  const handleAuthModeChange = (value: string) => {
    const index = parseInt(value, 10);
    setSelectedAuthModeIndex(index);
    setConnectionConfig({});
  };

  const handleConnectionConfigChange = (name: string, value: string) => {
    setConnectionConfig((prev) => ({ ...prev, [name]: value }));
  };

  const handleOAuth = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Get OAuth URL from backend (pass connection_config, displayName, baseUrl if present)
      const authData = await api.getAuthUrl(
        provider.provider,
        currentDefaultScopes,
        hasConnectionConfig ? connectionConfig : undefined,
        displayName.trim() || undefined,
        baseUrlOverride.trim() || selectedAuthMode?.baseUrl || undefined
      );

      // Open OAuth popup
      api.openOAuthPopup({
        url: authData.url,
        onSuccess: async () => {
          try {
            // Finalize connection
            const connection = await api.finalizeOAuthConnection(
              provider.provider,
              authData.connection_id
            );
            onSuccess(connection);
          } catch (err) {
            const message = (err as Error).message || 'Failed to finalize connection';
            setError(message);
            onError?.(message);
          } finally {
            setLoading(false);
          }
        },
        onError: (errMsg) => {
          setError(errMsg);
          onError?.(errMsg);
          setLoading(false);
        },
        onClose: () => {
          setLoading(false);
        },
      });
    } catch (err) {
      const message = (err as Error).message || 'Failed to start OAuth';
      setError(message);
      onError?.(message);
      setLoading(false);
    }
  }, [provider, connectionConfig, hasConnectionConfig, displayName, baseUrlOverride, currentDefaultScopes, selectedAuthMode, onSuccess, onError]);

  const handleCredentialsSubmit = useCallback(
    async (
      credentials: Record<string, string>,
      displayName?: string,
      advancedOverrides?: { base_url?: string },
      connectionConfigValues?: Record<string, string>
    ) => {
      setLoading(true);
      setError(null);

      try {
        const connection = await api.createConnection({
          provider: provider.provider,
          authMode: currentAuthMode,
          credentials,
          displayName,
          connectionConfig: connectionConfigValues,
          advancedOverrides,
        });
        onSuccess(connection);
      } catch (err) {
        const message = (err as Error).message || 'Failed to create connection';
        setError(message);
        onError?.(message);
      } finally {
        setLoading(false);
      }
    },
    [provider, onSuccess, onError]
  );

  return (
    <div className="space-y-6">
      {/* Provider header */}
      <div className="flex items-center gap-4">
        <ProviderLogo
          provider={provider.provider}
          displayName={provider.display_name}
          logoUrl={provider.logo}
          size="lg"
        />
        <div className="flex-1">
          <h3 className="font-semibold text-lg">
            {provider.display_name || provider.provider}
          </h3>
          <p className="text-sm text-muted-foreground">
            {selectedAuthMode?.label || getAuthModeLabel(currentAuthMode)}
          </p>
        </div>
      </div>

      {/* Auth mode selector (when multiple modes available) */}
      {hasMultipleAuthModes && (
        <div className="space-y-2">
          <Label>Authentication Method</Label>
          <Select
            value={selectedAuthModeIndex.toString()}
            onValueChange={handleAuthModeChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select authentication method" />
            </SelectTrigger>
            <SelectContent>
              {authModes.map((mode, index) => (
                <SelectItem key={index} value={index.toString()}>
                  {mode.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Auth form based on mode */}
      {isOAuth ? (
        <div className="space-y-4">
          {/* Connection config fields for OAuth providers */}
          {hasConnectionConfig && currentConnectionConfig && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Please provide the following details to connect with {provider.display_name || provider.provider}:
              </p>
              {currentConnectionConfig.map((field) => (
                <div key={field.name} className="space-y-2">
                  <Label htmlFor={field.name}>
                    {field.label}
                    {field.required && <span className="text-destructive ml-1">*</span>}
                  </Label>
                  <div className="flex items-center">
                    {field.prefix && (
                      <span className="text-sm text-muted-foreground bg-muted px-2 py-2 rounded-l-md border border-r-0">
                        {field.prefix}
                      </span>
                    )}
                    <Input
                      id={field.name}
                      placeholder={field.placeholder}
                      value={connectionConfig[field.name] || ''}
                      onChange={(e) => handleConnectionConfigChange(field.name, e.target.value)}
                      className={field.prefix || field.suffix ? 'rounded-none' : ''}
                    />
                    {field.suffix && (
                      <span className="text-sm text-muted-foreground bg-muted px-2 py-2 rounded-r-md border border-l-0">
                        {field.suffix}
                      </span>
                    )}
                  </div>
                  {field.description && (
                    <p className="text-xs text-muted-foreground">{field.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {!hasConnectionConfig && (
            <p className="text-sm text-muted-foreground">
              Click below to connect with {provider.display_name || provider.provider}.
              You&apos;ll be redirected to authorize access.
            </p>
          )}

          {/* Display name input */}
          <div className="space-y-2">
            <Label htmlFor="displayName">Connection Name (optional)</Label>
            <Input
              id="displayName"
              placeholder={`My ${provider.display_name || provider.provider} Account`}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Give this connection a name to distinguish it from others
            </p>
          </div>

          {/* Advanced Options */}
          <div className="border rounded-md">
            <button
              type="button"
              className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-left hover:bg-muted/50"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronRight className="size-4" />
              )}
              Advanced Options
            </button>
            {showAdvanced && (
              <div className="px-3 pb-3 space-y-2">
                <Label htmlFor="baseUrl">Custom Base URL (optional)</Label>
                <Input
                  id="baseUrl"
                  placeholder="https://api.example.com"
                  value={baseUrlOverride}
                  onChange={(e) => setBaseUrlOverride(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  For self-hosted or regional deployments. Leave empty for default.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleOAuth}
              disabled={loading || !isConnectionConfigValid()}
            >
              {loading ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <ExternalLink className="mr-2 size-4" />
              )}
              Connect with {provider.display_name || provider.provider}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {currentCredentials && currentCredentials.length > 0 ? (
            <CredentialsForm
              fields={currentCredentials}
              connectionConfigFields={currentConnectionConfig}
              onSubmit={handleCredentialsSubmit}
              onCancel={onCancel}
              loading={loading}
              providerName={provider.display_name || provider.provider}
            />
          ) : (
            <div className="text-center py-4">
              <p className="text-muted-foreground">
                No credentials required for this provider.
              </p>
              <div className="flex justify-center gap-2 mt-4">
                <Button variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  onClick={() => handleCredentialsSubmit({})}
                  disabled={loading}
                >
                  {loading && <Loader2 className="mr-2 size-4 animate-spin" />}
                  Connect
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Provider docs links */}
      {(provider.docs_url || provider.docs_connect || provider.setup_guide_url) && (
        <div className="text-xs text-center text-muted-foreground space-y-1">
          {provider.setup_guide_url && (
            <p>
              <a
                href={provider.setup_guide_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline text-primary"
              >
                📖 Setup Guide
              </a>
            </p>
          )}
          {provider.docs_connect && (
            <p>
              <a
                href={provider.docs_connect}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                How to get credentials
              </a>
            </p>
          )}
          {provider.docs_url && (
            <p>
              <a
                href={provider.docs_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                {provider.display_name || provider.provider} API documentation
              </a>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
